"use client";

import React from "react";
import { X, CheckCircle2, AlertCircle, Loader2, Clock } from "lucide-react";
import { Button } from "./button";

export type StatusType = 'loading' | 'success' | 'error' | 'warning' | 'info';

export interface StatusModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  message: string;
  status: StatusType;
  progress?: number; // 0-100 for progress bar
  showProgress?: boolean;
  primaryAction?: {
    label: string;
    onClick: () => void;
    variant?: 'default' | 'destructive' | 'outline' | 'secondary' | 'ghost' | 'link';
  };
  secondaryAction?: {
    label: string;
    onClick: () => void;
    variant?: 'default' | 'destructive' | 'outline' | 'secondary' | 'ghost' | 'link';
  };
  autoClose?: boolean;
  autoCloseDelay?: number; // in milliseconds
  showCloseButton?: boolean;
  size?: 'sm' | 'md' | 'lg';
}

const getStatusConfig = (status: StatusType) => {
  switch (status) {
    case 'loading':
      return {
        icon: Loader2,
        iconClass: 'text-blue-600 animate-spin',
        bgClass: 'bg-blue-50',
        borderClass: 'border-blue-200',
        textClass: 'text-blue-800'
      };
    case 'success':
      return {
        icon: CheckCircle2,
        iconClass: 'text-green-600',
        bgClass: 'bg-green-50',
        borderClass: 'border-green-200',
        textClass: 'text-green-800'
      };
    case 'error':
      return {
        icon: AlertCircle,
        iconClass: 'text-red-600',
        bgClass: 'bg-red-50',
        borderClass: 'border-red-200',
        textClass: 'text-red-800'
      };
    case 'warning':
      return {
        icon: AlertCircle,
        iconClass: 'text-yellow-600',
        bgClass: 'bg-yellow-50',
        borderClass: 'border-yellow-200',
        textClass: 'text-yellow-800'
      };
    case 'info':
      return {
        icon: Clock,
        iconClass: 'text-blue-600',
        bgClass: 'bg-blue-50',
        borderClass: 'border-blue-200',
        textClass: 'text-blue-800'
      };
    default:
      return {
        icon: Clock,
        iconClass: 'text-gray-600',
        bgClass: 'bg-gray-50',
        borderClass: 'border-gray-200',
        textClass: 'text-gray-800'
      };
  }
};

const getSizeClasses = (size: 'sm' | 'md' | 'lg') => {
  switch (size) {
    case 'sm':
      return 'max-w-md';
    case 'lg':
      return 'max-w-2xl';
    default:
      return 'max-w-lg';
  }
};

export function StatusModal({
  isOpen,
  onClose,
  title,
  message,
  status,
  progress,
  showProgress = false,
  primaryAction,
  secondaryAction,
  autoClose = false,
  autoCloseDelay = 3000,
  showCloseButton = true,
  size = 'md'
}: StatusModalProps) {
  
  const statusConfig = getStatusConfig(status);
  const IconComponent = statusConfig.icon;

  // Auto-close functionality
  React.useEffect(() => {
    if (autoClose && isOpen && status !== 'loading') {
      const timer = setTimeout(() => {
        onClose();
      }, autoCloseDelay);

      return () => clearTimeout(timer);
    }
  }, [autoClose, isOpen, status, autoCloseDelay, onClose]);

  // Close on escape key
  React.useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/30"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className={`relative ${getSizeClasses(size)} w-full mx-4 bg-white rounded-lg shadow-xl border ${statusConfig.borderClass}`}>
        {/* Header */}
        <div className={`flex items-center justify-between p-6 ${statusConfig.bgClass} rounded-t-lg`}>
          <div className="flex items-center gap-3">
            <IconComponent className={`h-6 w-6 ${statusConfig.iconClass}`} />
            <h2 className={`text-lg font-semibold ${statusConfig.textClass}`}>
              {title}
            </h2>
          </div>
          {showCloseButton && (
            <button
              onClick={onClose}
              className="p-1 hover:bg-white/50 rounded-md transition-colors"
              aria-label="Close modal"
            >
              <X className="h-5 w-5 text-gray-500" />
            </button>
          )}
        </div>

        {/* Content */}
        <div className="p-6">
          <p className="text-gray-700 mb-4 whitespace-pre-line">{message}</p>
          
          {/* Progress Bar */}
          {showProgress && progress !== undefined && (
            <div className="mb-4">
              <div className="flex justify-between text-sm text-gray-600 mb-2">
                <span>Progress</span>
                <span>{Math.round(progress)}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className={`h-2 rounded-full transition-all duration-300 ${
                    status === 'error' ? 'bg-red-500' : 
                    status === 'success' ? 'bg-green-500' : 
                    'bg-blue-500'
                  }`}
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          )}

          {/* Actions */}
          {(primaryAction || secondaryAction) && (
            <div className="flex gap-3 justify-end mt-6">
              {secondaryAction && (
                <Button
                  variant={secondaryAction.variant || 'outline'}
                  onClick={secondaryAction.onClick}
                  disabled={status === 'loading'}
                >
                  {secondaryAction.label}
                </Button>
              )}
              {primaryAction && (
                <Button
                  variant={primaryAction.variant || 'default'}
                  onClick={primaryAction.onClick}
                  disabled={status === 'loading'}
                >
                  {primaryAction.label}
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
